// @flow

import React, { useState } from 'react';
import * as Yup from 'yup';
import { i18n, Analytics } from 'shared/utils';
import { createFragmentContainer, graphql } from 'react-relay';
import * as Actions from 'main-app/store/Actions';
import AddVendorMutation from 'main-app/mutations/AddVendor';
import UpdateVendorMutation from 'main-app/mutations/UpdateVendor';
import AddVendorItemPriceMutation from 'main-app/mutations/AddVendorItemPrice';
import UpdateVendorItemPriceMutation from 'main-app/mutations/UpdateVendorItemPrice';
import DeleteVendorItemPriceMutation from 'main-app/mutations/DeleteVendorItemPrice';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import SelectItemField from 'shared/components/form/SelectItemField';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
} from 'shared/components/form';
import styled from 'styled-components';
import type { AddEditVendorModal_vendor as VendorFragment } from './__generated__/AddEditVendorModal_vendor';

type Props = {
  onClose: () => void,
  onSuccess?: Object => void,
  vendor: ?VendorFragment,
};

const LANGUAGE = {
  update: {
    title: i18n.t('Edit Vendor'),
    submitButton: i18n.t('Update Vendor'),
    successMessage: i18n.t('Vendor Successfully Updated'),
  },
  add: {
    title: i18n.t('Create Vendor'),
    submitButton: i18n.t('Create Vendor'),
    successMessage: i18n.t('Vendor Successfully Created'),
  },
};

const PricingSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const PricingSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #1a1a1a;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: 250px 120px 120px 80px 40px;
  gap: 12px;
  margin-bottom: 8px;
`;

const GridHeader = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #666;
  padding-bottom: 4px;
  display: flex;
  align-items: flex-end;
  height: 20px;
  position: relative;
`;

const TooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: help;
`;

const TooltipIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #999;
  color: white;
  font-size: 10px;
  font-weight: bold;
`;

const TooltipText = styled.span`
  visibility: hidden;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 8px 12px;
  background: #333;
  color: white;
  font-size: 12px;
  font-weight: normal;
  border-radius: 4px;
  white-space: nowrap;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #333;
  }

  ${TooltipWrapper}:hover & {
    visibility: visible;
    opacity: 1;
  }
`;

const GridCell = styled.div`
  display: flex;
  align-items: flex-start;
  
  > div {
    width: 100%;
    margin: 0;
    
    /* Override any label margins from child components */
    label {
      display: none;
    }
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    margin: 0;
  }
`;

const RemoveButton = styled.button`
  background: transparent;
  color: #ff4444;
  border: none;
  cursor: pointer;
  font-size: 28px;
  line-height: 1;
  height: 38px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;

  &:hover {
    color: #cc0000;
    background: rgba(255, 68, 68, 0.1);
    border-radius: 4px;
  }
`;

const AddRowButton = styled.button`
  background: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;

  &:hover {
    background: #357abd;
  }
`;

const StarRatingContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 8px;
`;

const Star = styled.span`
  font-size: 24px;
  cursor: pointer;
  color: ${props => (props.filled ? '#ffa500' : '#d0d0d0')};
  transition: color 0.2s;

  &:hover {
    color: #ffa500;
  }
`;

const AddEditVendorModal = ({ onClose, onSuccess, vendor }: Props) => {
  const language = LANGUAGE[vendor ? 'update' : 'add'];

  const [pricingRows, setPricingRows] = useState(
    vendor?.itemPrices?.edges
      ?.filter(edge => edge && edge.node) // Filter out null/undefined edges
      ?.map(edge => ({
        id: edge.node.id,
        item: {
          value: edge.node.item.id,
          label: edge.node.item.name,
          data: edge.node.item,
        },
        unitPrice: edge.node.unitPrice,
        leadTimeDays: edge.node.leadTimeDays || '',
        isPreferred: edge.node.isPreferred || false,
      })) || []
  );
  
  const [pricingChanged, setPricingChanged] = useState(false);
  const [deletedPriceIds, setDeletedPriceIds] = useState([]);

  const addPricingRow = () => {
    setPricingRows([
      ...pricingRows,
      {
        id: null,
        item: null,
        unitPrice: '',
        leadTimeDays: '',
        isPreferred: false,
      },
    ]);
    setPricingChanged(true);
  };

  const removePricingRow = index => {
    const row = pricingRows[index];
    
    // If this row has an ID, it exists in the database and needs to be deleted
    if (row.id) {
      setDeletedPriceIds([...deletedPriceIds, row.id]);
    }
    
    setPricingRows(pricingRows.filter((_, i) => i !== index));
    setPricingChanged(true);
  };

  const updatePricingRow = (index, field, value) => {
    const updated = [...pricingRows];
    updated[index][field] = value;
    setPricingRows(updated);
    setPricingChanged(true);
  };

  return (
    <Modal maxWidth={900}>
      <Formik
        initialValues={{
          vendorNumber: vendor?.vendorNumber || '',
          name: vendor?.name || '',
          email: vendor?.email || '',
          phone: vendor?.phone || '',
          address: vendor?.address || '',
          city: vendor?.city || '',
          state: vendor?.state || '',
          zipCode: vendor?.zipCode || '',
          country: vendor?.country || '',
          paymentTerms: vendor?.paymentTerms || '',
          rating: vendor?.rating || 5,
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .trim()
            .required('Required'),
          email: Yup.string()
            .email('Invalid Email')
            .nullable(),
          phone: Yup.string().nullable(),
          paymentTerms: Yup.string().nullable(),
          rating: Yup.number()
            .min(1, 'Rating must be between 1 and 5')
            .max(5, 'Rating must be between 1 and 5')
            .required('Required'),
        })}
        onSubmit={async (values: *, { setSubmitting }: *) => {
          setSubmitting(true);

          const mutation = vendor ? UpdateVendorMutation : AddVendorMutation;

          const input: any = {
            name: values.name,
            email: values.email || null,
            phone: values.phone || null,
            address: values.address || null,
            city: values.city || null,
            state: values.state || null,
            zipCode: values.zipCode || null,
            country: values.country || null,
            paymentTerms: values.paymentTerms || null,
            rating: parseInt(values.rating, 10),
          };

          if (vendor) {
            input.id = vendor.id;
          }

          try {
            const response = await mutation.commit({
              variables: {
                input,
              },
            });

            const newVendor = response.updateVendor
              ? response.updateVendor.vendor
              : response.addVendor.vendorEdge.node;

            // Process all item price changes in parallel for better performance
            const mutations = [];

            // Delete removed item prices
            deletedPriceIds.forEach(deletedId => {
              mutations.push(
                DeleteVendorItemPriceMutation.commit({
                  variables: {
                    input: { id: deletedId },
                  },
                })
              );
            });

            // Update and add item pricing
            pricingRows.forEach(row => {
              // Skip incomplete rows
              if (!row.item || !row.unitPrice || !row.leadTimeDays) return;

              if (row.id) {
                // Update existing pricing
                mutations.push(
                  UpdateVendorItemPriceMutation.commit({
                    variables: {
                      input: {
                        id: row.id,
                        unitPrice: parseFloat(row.unitPrice),
                        leadTimeDays: parseInt(row.leadTimeDays, 10),
                        isPreferred: row.isPreferred || false,
                      },
                    },
                  })
                );
              } else {
                // Add new pricing
                mutations.push(
                  AddVendorItemPriceMutation.commit({
                    variables: {
                      input: {
                        vendorId: newVendor.id,
                        itemId: row.item.value,
                        unitPrice: parseFloat(row.unitPrice),
                        leadTimeDays: parseInt(row.leadTimeDays, 10),
                        isPreferred: row.isPreferred || false,
                      },
                    },
                  })
                );
              }
            });

            // Execute all mutations in parallel and wait for all to complete
            await Promise.all(mutations);

            onClose();

            if (onSuccess) {
              onSuccess(newVendor);
            }

            Analytics.trackEvent(`${vendor ? 'Update' : 'Create'} Vendor`, {
              name: values.name,
              email: values.email,
              rating: values.rating,
            });

            Actions.alertNotification(language.successMessage, 'success');
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({
          values,
          errors,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              {vendor && (
                <FieldGroup
                  name="vendorNumber"
                  label={i18n.t('Vendor Number')}
                  placeholder={i18n.t('Auto-generated')}
                  disabled
                  error={errors.vendorNumber}
                />
              )}

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="name"
                    label={i18n.t('Name')}
                    placeholder={i18n.t('Vendor Name')}
                    error={errors.name}
                  />
                }
                right={
                  <FieldGroup
                    name="email"
                    label={i18n.t('Email')}
                    placeholder={i18n.t('vendor@example.com')}
                    error={errors.email}
                  />
                }
              />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="phone"
                    label={i18n.t('Phone')}
                    placeholder={i18n.t('Phone Number')}
                    error={errors.phone}
                  />
                }
                right={
                  <FieldGroup
                    name="paymentTerms"
                    label={i18n.t('Payment Terms')}
                    placeholder={i18n.t('e.g., Net 30')}
                    error={errors.paymentTerms}
                  />
                }
              />

              <FieldGroup
                name="address"
                label={i18n.t('Address')}
                placeholder={i18n.t('Street Address')}
                error={errors.address}
              />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="city"
                    label={i18n.t('City')}
                    placeholder={i18n.t('City')}
                    error={errors.city}
                  />
                }
                right={
                  <FieldGroup
                    name="state"
                    label={i18n.t('State/Province')}
                    placeholder={i18n.t('State')}
                    error={errors.state}
                  />
                }
              />

              <FieldGroupRow
                left={
                  <FieldGroup
                    name="zipCode"
                    label={i18n.t('ZIP/Postal Code')}
                    placeholder={i18n.t('ZIP Code')}
                    error={errors.zipCode}
                  />
                }
                right={
                  <FieldGroup
                    name="country"
                    label={i18n.t('Country')}
                    placeholder={i18n.t('Country')}
                    error={errors.country}
                  />
                }
              />

              <FieldGroup
                name="rating"
                label={i18n.t('Vendor Rating (1-5 stars)')}
                error={errors.rating}
              >
                <StarRatingContainer>
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      filled={star <= values.rating}
                      onClick={() => setFieldValue('rating', star)}
                    >
                      ★
                    </Star>
                  ))}
                </StarRatingContainer>
              </FieldGroup>

              <PricingSection>
                <PricingSectionTitle>
                  {i18n.t('Item Pricing')}
                </PricingSectionTitle>

                {/* Grid Headers */}
                <PricingGrid>
                  <GridHeader>{i18n.t('Item')}</GridHeader>
                  <GridHeader>{i18n.t('Unit Price')}</GridHeader>
                  <GridHeader>{i18n.t('Lead Time (Days)')}</GridHeader>
                  <GridHeader>
                    <TooltipWrapper>
                      {i18n.t('Preferred')}
                      <TooltipIcon>?</TooltipIcon>
                      <TooltipText>
                        Mark as preferred vendor for this item when auto-generating purchase orders
                      </TooltipText>
                    </TooltipWrapper>
                  </GridHeader>
                  <GridHeader></GridHeader>
                </PricingGrid>

                {/* Grid Rows */}
                {pricingRows.map((row, index) => (
                  <PricingGrid key={index}>
                    <GridCell>
                      <SelectItemField
                        rawField
                        name={`pricing-item-${index}`}
                        value={row.item}
                        onChange={item => updatePricingRow(index, 'item', item)}
                        disabled={!!row.id}
                      />
                    </GridCell>
                    <GridCell>
                      <FieldGroup
                        rawField
                        name={`pricing-price-${index}`}
                        placeholder="0.00"
                        type="number"
                        value={row.unitPrice}
                        onChange={value =>
                          updatePricingRow(index, 'unitPrice', value)
                        }
                      />
                    </GridCell>
                    <GridCell>
                      <FieldGroup
                        rawField
                        name={`pricing-leadtime-${index}`}
                        placeholder="7"
                        type="number"
                        value={row.leadTimeDays}
                        onChange={value =>
                          updatePricingRow(index, 'leadTimeDays', value)
                        }
                      />
                    </GridCell>
                    <CheckboxCell>
                      <input
                        type="checkbox"
                        checked={row.isPreferred}
                        onChange={e =>
                          updatePricingRow(index, 'isPreferred', e.target.checked)
                        }
                      />
                    </CheckboxCell>
                    <RemoveButton
                      type="button"
                      onClick={() => removePricingRow(index)}
                      title={i18n.t('Remove')}
                    >
                      ×
                    </RemoveButton>
                  </PricingGrid>
                ))}

                <AddRowButton type="button" onClick={addPricingRow}>
                  {i18n.t('+ Add Item Pricing')}
                </AddRowButton>
              </PricingSection>
            </ModalBody>
            <ModalFooter>
              <Button
                type="submit"
                theme="blue"
                disabled={!isValid && !pricingChanged}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.submitButton}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

AddEditVendorModal.defaultProps = {
  onSuccess: undefined,
};

export default createFragmentContainer(AddEditVendorModal, {
  vendor: graphql`
    fragment AddEditVendorModal_vendor on Vendor {
      id
      vendorNumber
      name
      email
      phone
      address
      city
      state
      zipCode
      country
      paymentTerms
      rating
      itemPrices {
        edges {
          node {
            id
            item {
              id
              name
            }
            unitPrice
            leadTimeDays
            isPreferred
          }
        }
      }
    }
  `,
});
